export type IpLocation = {
  ip?: string;
  city?: string;
  region?: string;
  country?: string;
  loc?: string;
  latitude?: number;
  longitude?: number;
  org?: string;
  source?: "ipinfo" | "ipapi" | "ipwhois";
  error?: string;
};

const SESSION_KEY = "ipLocation";

function parseLoc(loc?: string): { latitude?: number; longitude?: number } {
  if (!loc) return {};
  const parts = loc.split(",").map((s) => s.trim());
  const lat = Number(parts[0]);
  const lon = Number(parts[1]);
  if (Number.isFinite(lat) && Number.isFinite(lon)) return { latitude: lat, longitude: lon };
  return {};
}

export function withTimeout<T>(promise: Promise<T>, ms = 4000): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => {
      reject(new Error("timeout"));
    }, ms);
    promise
      .then((res) => {
        clearTimeout(id);
        resolve(res);
      })
      .catch((err) => {
        clearTimeout(id);
        reject(err);
      });
  });
}

async function fetchJson(url: string, ms = 4000): Promise<any> {
  const controller = new AbortController();
  const signal = controller.signal;

  const fetchPromise = fetch(url, { signal }).then(async (r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });

  const winner = withTimeout(fetchPromise, ms).catch((err) => {
    // Abort fetch if timeout or error
    try {
      controller.abort();
    } catch {}
    throw err;
  });

  return winner;
}

export async function fetchIpLocation(): Promise<IpLocation> {
  // Check cache
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed: IpLocation = JSON.parse(raw) as IpLocation;
      return parsed;
    }
  } catch {}

  const token = (typeof import.meta !== "undefined" ? (import.meta as any).env?.VITE_IPINFO_TOKEN : undefined) ??
    (typeof process !== "undefined" ? (process as any)?.env?.REACT_APP_IPINFO_API_KEY : undefined);

  // Try providers in order
  const errors: string[] = [];

  // 1) ipinfo (requires token)
  if (token) {
    try {
      const url = `https://ipinfo.io/json?token=${encodeURIComponent(token)}`;
      const res = await fetchJson(url);
      const base: IpLocation = {
        ip: typeof res.ip === "string" ? res.ip : undefined,
        city: typeof res.city === "string" ? res.city : undefined,
        region: typeof res.region === "string" ? res.region : undefined,
        country: typeof res.country === "string" ? res.country : undefined,
        loc: typeof res.loc === "string" ? res.loc : undefined,
        org: typeof res.org === "string" ? res.org : undefined,
        source: "ipinfo",
      };
      const coords = parseLoc(base.loc);
      const final = { ...base, ...coords };
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(final));
      } catch {}
      return final;
    } catch (err) {
      errors.push(`ipinfo: ${(err as Error).message}`);
    }
  }

  // 2) ipapi.co
  try {
    const url = `https://ipapi.co/json/`;
    const res = await fetchJson(url);
    const base: IpLocation = {
      ip: typeof res.ip === "string" ? res.ip : undefined,
      city: typeof res.city === "string" ? res.city : undefined,
      region: typeof res.region === "string" ? res.region : undefined,
      country: (typeof res.country_name === "string" ? res.country_name : typeof res.country === "string" ? res.country : undefined) as string | undefined,
      latitude: typeof res.latitude === "number" ? res.latitude : typeof res.latitude === "string" && res.latitude ? Number(res.latitude) : undefined,
      longitude: typeof res.longitude === "number" ? res.longitude : typeof res.longitude === "string" && res.longitude ? Number(res.longitude) : undefined,
      org: typeof res.org === "string" ? res.org : undefined,
      source: "ipapi",
    };
    const final = base;
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(final));
    } catch {}
    return final;
  } catch (err) {
    errors.push(`ipapi: ${(err as Error).message}`);
  }

  // 3) ipwho.is
  try {
    const url = `https://ipwho.is/`;
    const res = await fetchJson(url);
    // ipwho.is returns success flag
    if (res && res.success === false) throw new Error(res.message || "failed");
    const base: IpLocation = {
      ip: typeof res.ip === "string" ? res.ip : undefined,
      city: typeof res.city === "string" ? res.city : undefined,
      region: typeof res.region === "string" ? res.region : undefined,
      country: typeof res.country === "string" ? res.country : undefined,
      latitude: typeof res.latitude === "number" ? res.latitude : typeof res.latitude === "string" && res.latitude ? Number(res.latitude) : undefined,
      longitude: typeof res.longitude === "number" ? res.longitude : typeof res.longitude === "string" && res.longitude ? Number(res.longitude) : undefined,
      org: typeof res.org === "string" ? res.org : undefined,
      source: "ipwhois",
    };
    const final = base;
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(final));
    } catch {}
    return final;
  } catch (err) {
    errors.push(`ipwhois: ${(err as Error).message}`);
  }

  const fallback: IpLocation = { error: "No se pudo obtener ubicación" };
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(fallback));
  } catch {}
  return { ...fallback, error: `No se pudo obtener ubicación (${errors.join(" | ")})` };
}

export function clearIpLocationCache(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {}
}
